import React from 'react';
import { CapacitorHttp } from '@capacitor/core';
import { OAuth2Client } from '@byteowls/capacitor-oauth2';
import { Browser } from '@capacitor/browser';
import { get, set, remove, _iamKey, getAuthTypePreference } from '../util/Preferences';
import { isPlatform } from '@ionic/react';
import { clearApiOptionsData, clearApiSession, getApiSession, _apiSessionKey } from './ApiOptionsHelper';
import {
    isBiometricEnabled,
    storeBiometricCredential,
    getBiometricCredential,
    deleteBiometricCredential,
    BIOMETRIC_PORTAL_SERVER,
} from './Biometric';

// When true, biometric sign-in stores the long-lived refresh token (B2C
// offline_access) and silently mints fresh access tokens on relaunch, so
// biometric sign-in keeps working for days - including after a normal logout.
// When false, it stores the short-lived access token (biometric only skips the
// login screen while that token is still valid). B2C refresh-token support was
// confirmed for the mobile client - see BIOMETRIC-SETUP.md.
export const REFRESH_TOKEN_AVAILABLE = true;

// The refresh token from the current session's B2C sign-in, held in memory so
// biometric enrollment (which happens right after sign-in) can store the
// long-lived refresh token instead of the short-lived access token. Without
// this, biometric restore would try to use an access token as a refresh token
// and fail on the next launch.
let sessionRefreshToken: string | null = null;
// Same idea for the Portal sign-in's refresh token, so biometric can silently
// refresh the Portal API token too (needed so a long timeout never forces the
// user back to the email screen).
let sessionPortalRefreshToken: string | null = null;

const BEARER_TOKEN = "BearerToken";
const BEARER_TOKEN_PARSED = "BearerTokenParsed";
const BEARER_TOKEN_PORTAL = "BearerTokenPortal";
const BEARER_TOKEN_PORTAL_PARSED = "BearerTokenPortalParsed";
const PORTAL_API_TOKEN = "PortalApiToken"; 
const PORTAL_API_TOKEN_PARSED = "PortalApiTokenParsed";
const PORTAL_USER_UNIQUEID = "PortalUserUniqueId";
const B2C_SCOPE_CURRENT = "CurrentB2CScope";
export const ID_TOKEN = "IdToken";
// Records whether the CURRENT session was established by a SILENT biometric
// restore (token endpoint only - no browser, so no B2C SSO session/cookie is
// created). Set only on a successful biometric restore, cleared on any
// interactive browser sign-in, and consumed at sign-out. When set, logout skips
// the B2C end-session page (there is nothing to sign out) and returns straight
// to the Identity screen instead of stranding the user on a dead-end "select an
// account" / "you signed out" page. When ABSENT - interactive login, web/PWA, or
// a session that predates this marker - logout behaves exactly as before and
// opens the sign-out page. Defaulting to "absent = old behavior" keeps every
// existing flow (including web and the shared-device SSO sign-out) unchanged.
const SESSION_VIA_BIOMETRIC = "SessionViaBiometric";

interface AuthContent {
    loggedIn: boolean | undefined;
    setLoggedIn: ((loggedIn: boolean) => void) | undefined,
}

export const AuthContext = React.createContext<AuthContent>({
    loggedIn: false,
    setLoggedIn: undefined
});

export const signin = async () => {
    const oAuthOptions = await getOAuthOptions(false);
    return await OAuth2Client.authenticate(oAuthOptions).then(async response => {
        let accessTokenResponse = response['access_token_response'];
        let jwtToken = accessTokenResponse['access_token'];
        await storeAuthData(jwtToken);
        //store b2c scope used
        await set(B2C_SCOPE_CURRENT, oAuthOptions.scope);
        //hold this session's refresh token so enrollment can store the durable credential
        sessionRefreshToken = accessTokenResponse['refresh_token'] || null;
        //always cache the credential so biometric restore is reliable on first enroll
        await cacheEamCredential(accessTokenResponse);
        //interactive browser sign-in: clear any biometric marker so logout runs the B2C sign-out page
        await remove(SESSION_VIA_BIOMETRIC);
        return true;
    }).catch(reason => {
        console.log('B2C SIGN IN CALL ERROR');
        console.log('Auth failed: ' + reason);

        return false;
    });
}

export const signinPortal = async () => {
    const currentUrl = window.location.href;
    // Bug #879938: a launch from the Tenant Portal (flagged with the 'portalLaunch'
    // marker by UserIdentity.checkIAMQueryParam) carries no email/login_hint, so B2C
    // would otherwise silently reuse an existing SSO session and sign the launcher in
    // as the *previous* user. Force a fresh sign-in on that path only, and consume the
    // one-shot marker immediately. A normal app relaunch (no marker) and a password-
    // reset return are left untouched, so returning users are never re-prompted.
    const isPortalLaunch = sessionStorage.getItem('portalLaunch') === 'true';
    if (isPortalLaunch) {
        sessionStorage.removeItem('portalLaunch');
    }
    const isPasswordReset = currentUrl.includes('access_denied') && currentUrl.includes('The+user+has+forgotten+their+password');
    var oauthOptions = await getOAuthOptions(true, isPortalLaunch && !isPasswordReset);
    if (isPasswordReset) {
        let authConfig = await getAuthConfig();
        if (authConfig)
            oauthOptions.authorizationBaseUrl = authConfig.AUTHORIZATION_PASSWORD_RESET_BASE_URL;
    }
    if (isPlatform('hybrid') && sessionStorage.getItem('forceLoginPrompt') === 'true') {
        sessionStorage.removeItem('forceLoginPrompt');
        if (oauthOptions.android) {
            oauthOptions.android.additionalParameters = { ...oauthOptions.android.additionalParameters, prompt: 'login' };
        }
        if (oauthOptions.ios) {
            oauthOptions.ios.additionalParameters = { ...oauthOptions.ios.additionalParameters, prompt: 'login' };
        }
    }
    return await OAuth2Client.authenticate(oauthOptions).then(async response => {
        let accessTokenResponse = response['access_token_response'];
        let jwtToken = accessTokenResponse['access_token'];
        let idToken = accessTokenResponse['id_token'];
        await storePortalAuthData(jwtToken, idToken);
        // hold this session's portal refresh token so enrollment/biometric can
        // silently refresh the portal token later
        sessionPortalRefreshToken = accessTokenResponse['refresh_token'] || null;
        await cachePortalCredential(accessTokenResponse);
        //interactive browser sign-in: clear any biometric marker so logout runs the B2C sign-out page
        await remove(SESSION_VIA_BIOMETRIC);
        return true;
    }).catch(reason => {
        console.log('(PORTAL) B2C SIGN IN CALL ERROR');
        console.log('(PORTAL) Auth failed: ' + reason);

        return false;
    });
}

export const signout = async (relogin: boolean) => {
    // A normal logout intentionally KEEPS the biometric credential so the user
    // can sign back in quickly with biometrics (like banking apps). Removing it
    // is a deliberate, separate action ("Sign out & remove biometric sign-in"),
    // which clears the opt-in + credential before calling this.
    if (!relogin) {
        // A silent biometric restore never opened the browser, so there is no B2C
        // SSO session/cookie to end. Opening the B2C sign-out page in that case is
        // a dead end (empty "select an account" / "you signed out" page that just
        // waits for the user to close it). Only skip when we KNOW the session came
        // from biometric; every other case (interactive login, web/PWA, or a
        // session that predates this marker) falls through to the normal page.
        const viaBiometric = (await get(SESSION_VIA_BIOMETRIC)) === true;
        if (viaBiometric) {
            await clearAuthData();
            await remove(SESSION_VIA_BIOMETRIC);
            return false; // did NOT open the interactive sign-out page
        }
        //logout from b2c
        const logoutUrl = encodeURI(await getLogoutUrl());
        // Clear session data before navigating away so it is not re-used on next app load
        await clearAuthData();
        // Open the B2C / IdP sign-out page and KEEP it open. On a shared device
        // (e.g. shared QA iPads) the user must be able to complete the SSO sign-out
        // on the IdP page so the federated cookie is cleared - otherwise the next
        // user is silently signed back in with the previous user's session.
        // We no longer force-close the browser on a fixed timer; the caller
        // (Logout.tsx) returns to UserIdentity only once the user dismisses it.
        await Browser.open({ url: logoutUrl, windowName: "_self" });
        return true; // opened the interactive sign-out page
    }

    return await OAuth2Client.logout(await getOAuthOptions(false)).then(async response => {
        await clearAuthData();
        return true;
    }).catch(reason => {
        console.log('Signout failed: ' + reason);
        return false;
    });
}

export const signoutPortal = async () => {
    return await OAuth2Client.logout(await getOAuthOptions(true)).then(async response => {
        await clearPortalAuthData();
        return true;
    }).catch(reason => {
        console.log('(PORTAL) Signout failed: ' + reason);
        return false;
    });
}

export const getBearerToken = async () => {
    return await get(BEARER_TOKEN).then(result => {
        if (!!!result) {
            console.log('Bearer token not found');
        }
        return result;
    });
}

export const getBearerTokenParsed = async () => {
    return await get(BEARER_TOKEN_PARSED).then(result => {
        if (!!!result) {
            console.log('Bearer token not found');
        }
        return result;
    });
}

export const getPortalBearerToken = async () => {
    return await get(BEARER_TOKEN_PORTAL).then(result => {
        if (!!!result) {
            console.log('Portal bearer token not found');
        }
        return result;
    });
}

export const getUserName = async (): Promise<string> => {
    return await get(BEARER_TOKEN_PARSED).then(token => {
        if (!!token) {
            let jwtParsed = JSON.parse(JSON.stringify(token));
            return jwtParsed.name;
        }
        return null;
    });
};

/**
 * Caches the EAM session credential in the device secure store after every B2C
 * sign-in, so it is always available for biometric restore later. In full mode
 * this always caches the long-lived refresh token (regardless of whether the
 * user has opted in yet) - this is what makes biometric enrollment reliable on
 * the very first attempt, even when enrollment happens in a later app session
 * where the in-memory refresh token would otherwise be gone. In interim mode it
 * only caches the short-lived access token when the user has already opted in.
 */
const cacheEamCredential = async (accessTokenResponse: any) => {
    try {
        const username = (await getUserName()) || 'eam-user';
        if (REFRESH_TOKEN_AVAILABLE) {
            const refreshToken = accessTokenResponse['refresh_token'];
            if (!refreshToken) {
                return;
            }
            await storeBiometricCredential(refreshToken, username);
            return;
        }
        if (!(await isBiometricEnabled())) {
            return;
        }
        const accessToken = accessTokenResponse['access_token'];
        if (!accessToken) {
            return;
        }
        await storeBiometricCredential(accessToken, username);
    } catch (error) {
        console.log('cacheEamCredential error:', error);
    }
};

/**
 * Caches the Portal refresh token in the secure store (under the portal key)
 * after every portal B2C sign-in, so biometric can silently refresh the portal
 * token later. Always caches in full mode (not gated on opt-in) for the same
 * first-attempt reliability reason as cacheEamCredential.
 */
const cachePortalCredential = async (accessTokenResponse: any) => {
    try {
        if (!REFRESH_TOKEN_AVAILABLE) {
            return;
        }
        const refreshToken = accessTokenResponse['refresh_token'];
        if (!refreshToken) {
            return;
        }
        const username = (await getUserName()) || 'eam-user';
        await storeBiometricCredential(refreshToken, username, BIOMETRIC_PORTAL_SERVER);
    } catch (error) {
        console.log('cachePortalCredential error:', error);
    }
};

/**
 * Confirms a credential is available for biometric sign-in at enrollment time
 * (right after a successful login, or when toggled on in Settings). In full mode
 * the refresh tokens are already cached at every sign-in (see cacheEamCredential
 * / cachePortalCredential), so enrollment just refreshes those cached copies from
 * the current session if it captured them, then verifies the EAM credential
 * exists. It never falls back to storing a short-lived access token, which was
 * the cause of biometric failing on the first attempt and only working on the
 * second. In interim mode it stores the current access token. Returns false if
 * no credential is available to enroll.
 */
export const persistBiometricCredentialForEnrollment = async (): Promise<boolean> => {
    try {
        const username = (await getUserName()) || 'eam-user';
        if (REFRESH_TOKEN_AVAILABLE) {
            // Refresh the cached copies from this session's tokens if we captured
            // them during a fresh login; otherwise rely on whatever was cached at
            // the last sign-in (cacheEamCredential / cachePortalCredential).
            if (sessionRefreshToken) {
                await storeBiometricCredential(sessionRefreshToken, username);
            }
            if (sessionPortalRefreshToken) {
                await storeBiometricCredential(sessionPortalRefreshToken, username, BIOMETRIC_PORTAL_SERVER);
            }
            const eamCred = await getBiometricCredential();
            if (!eamCred) {
                console.log('persistBiometricCredentialForEnrollment: no cached EAM credential available to enroll');
                return false;
            }
            console.log(
                `persistBiometricCredentialForEnrollment: enrolled from cached credential (refreshed=${!!sessionRefreshToken} portal=${!!sessionPortalRefreshToken})`
            );
            return true;
        }
        // Interim mode: store the current short-lived access token.
        const token = await getBearerToken();
        if (!token) {
            console.log('persistBiometricCredentialForEnrollment: no session token available to store');
            return false;
        }
        return await storeBiometricCredential(token, username);
    } catch (error) {
        console.log('persistBiometricCredentialForEnrollment error:', error);
        return false;
    }
};

/**
 * Removes any credential cached at login from the secure store UNLESS the user
 * has enrolled in biometric sign-in. Credentials are cached on every login so
 * that enrollment is reliable on the first attempt (see cacheEamCredential /
 * cachePortalCredential); this ensures we never retain that bearer token for a
 * user who did not opt in - e.g. after they decline the enrollment offer, or
 * defensively on the Identity screen when biometric is disabled. No-op when the
 * user is enrolled (their credential must survive) or on web (nothing is stored).
 */
export const clearCachedCredentialIfNotEnrolled = async (): Promise<void> => {
    try {
        if (await isBiometricEnabled()) {
            return;
        }
        await deleteBiometricCredential();
        await deleteBiometricCredential(BIOMETRIC_PORTAL_SERVER);
    } catch (error) {
        console.log('clearCachedCredentialIfNotEnrolled error:', error);
    }
};

/**
 * Whether a session can actually be restored from the biometric-stored
 * credential - used to decide whether prompting for biometrics is worthwhile.
 *
 * - REFRESH_TOKEN_AVAILABLE true: any stored refresh token is worth trying
 *   (the token endpoint is the real validity check).
 * - REFRESH_TOKEN_AVAILABLE false: the stored value is an access token, so it
 *   is only useful while unexpired - otherwise we skip the prompt and go
 *   straight to B2C rather than making the user authenticate twice.
 */
export const canRestoreBiometricSession = async (): Promise<boolean> => {
    const stored = await getBiometricCredential();
    if (!stored) {
        return false;
    }
    if (REFRESH_TOKEN_AVAILABLE) {
        return true;
    }
    try {
        const parsed = parseJwt(stored);
        return !!parsed && (new Date().valueOf() < parsed.exp * 1000);
    } catch (error) {
        console.log('canRestoreBiometricSession parse error:', error);
        return false;
    }
};

/**
 * Restores an EAM session from the biometric-stored credential.
 * Call only after the biometric prompt has succeeded.
 *
 * - REFRESH_TOKEN_AVAILABLE true: exchanges the stored refresh token for a
 *   fresh access token (true silent re-login, works long after app close).
 * - REFRESH_TOKEN_AVAILABLE false: restores the stored access token and
 *   reports whether it is still valid (interim behaviour).
 *
 * Returns true when a valid session is in place, false to fall back to B2C login.
 */
export const restoreSessionFromBiometric = async (): Promise<boolean> => {
    try {
        const stored = await getBiometricCredential();
        if (!stored) {
            return false;
        }
        if (REFRESH_TOKEN_AVAILABLE) {
            // Refresh the Portal token first (so downstream portal checks pass),
            // then the EAM token. Both must succeed for a clean silent restore -
            // this is what lets a long timeout re-login without the email screen.
            const portalOk = await refreshPortalTokenFromBiometric();
            // Where the EAM and Portal sign-ins resolve to the same client and
            // scope, the login path adopts the Portal token as the EAM token
            // (reusePortalSessionForEam) and caches the SAME refresh token in
            // both credential slots. Redeeming it a second time here would
            // present an already-spent token wherever the IdP rotates refresh
            // tokens - and refreshAccessTokenFromBiometric deletes the EAM
            // credential on failure, so biometric would stay broken until the
            // next interactive login. Adopt the Portal token that was just
            // refreshed instead of redeeming again. Falls through to the
            // original refresh when the scopes genuinely differ (B2C).
            let eamOk = false;
            if (portalOk) {
                eamOk = await reusePortalSessionForEam();
            }
            if (!eamOk) {
                eamOk = await refreshAccessTokenFromBiometric(stored);
            }
            const ok = portalOk && eamOk;
            if (ok) {
                // Mark this as a silent biometric session so logout can skip the
                // dead-end B2C sign-out page (no browser SSO session to end).
                await set(SESSION_VIA_BIOMETRIC, true);
            }
            return ok;
        }
        // Interim mode: the stored value is an access token.
        await storeAuthData(stored);
        await set(B2C_SCOPE_CURRENT, await getScope(false));
        const ok = await validateBearerToken();
        if (ok) {
            await set(SESSION_VIA_BIOMETRIC, true);
        }
        return ok;
    } catch (error) {
        console.log('restoreSessionFromBiometric error:', error);
        return false;
    }
};

/**
 * Resolves the B2C token endpoint for the current platform. The mobile code
 * exchange uses the platform-specific endpoint (see getOAuthOptions), so the
 * refresh must hit the same one - that guarantees the refresh token is redeemed
 * at the same tenant that issued it. The top-level ACCESS_TOKEN_ENDPOINT is a
 * last-resort fallback (it is empty in the current config).
 *
 * NOTE: the ANDROID and IOS endpoints in authConfig.json currently point at
 * different B2C tenants (apteanid vs eamb2cdev). That is a pre-existing config
 * inconsistency - confirm both are correct for each environment before relying
 * on full mode in production. This helper intentionally does not "correct" it;
 * it mirrors whatever the initial code exchange used.
 */
const getAccessTokenEndpoint = (authConfig: any): string => {
    if (isPlatform('android') && !!authConfig.ANDROID?.ACCESS_TOKEN_ENDPOINT) {
        return authConfig.ANDROID.ACCESS_TOKEN_ENDPOINT;
    }
    if (isPlatform('ios') && !!authConfig.IOS?.ACCESS_TOKEN_ENDPOINT) {
        return authConfig.IOS.ACCESS_TOKEN_ENDPOINT;
    }
    if (!!authConfig.WEB?.ACCESS_TOKEN_ENDPOINT) {
        return authConfig.WEB.ACCESS_TOKEN_ENDPOINT;
    }
    return authConfig.ACCESS_TOKEN_ENDPOINT || '';
};

/**
 * POSTs a refresh_token grant to the IdP token endpoint.
 *
 * Uses CapacitorHttp, NOT fetch(). The WebView runs on the http://localhost
 * origin, so a fetch() to the IdP is a cross-origin request and the IdP does not
 * send Access-Control-Allow-Origin for it - the browser blocks it before the
 * request is even made ("TypeError: Failed to fetch"). That is why biometric
 * restore never completed on a device. CapacitorHttp goes through the native
 * layer and is not subject to CORS, which is why every other API call in the app
 * (mobileBootstrap, abpLogin) already uses it.
 */
const postRefreshGrant = async (
    tokenEndpoint: string,
    clientId: string,
    refreshToken: string,
    scope: string
): Promise<{ ok: boolean; status: number; data: any }> => {
    const response = await CapacitorHttp.post({
        url: tokenEndpoint,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        data: {
            grant_type: 'refresh_token',
            client_id: clientId,
            refresh_token: refreshToken,
            scope: scope,
        },
    });
    //CapacitorHttp parses JSON responses; guard the string case defensively
    let data = response.data;
    if (typeof data === 'string') {
        try {
            data = JSON.parse(data);
        } catch {
            data = {};
        }
    }
    return {
        ok: response.status >= 200 && response.status < 300,
        status: response.status,
        data: data || {},
    };
};

/**
 * Exchanges a stored refresh token for a fresh access token against B2C.
 * Only reachable when REFRESH_TOKEN_AVAILABLE is true.
 */
const refreshAccessTokenFromBiometric = async (refreshToken: string): Promise<boolean> => {
    try {
        const authConfig = await getAuthConfig();
        if (!authConfig) {
            return false;
        }
        const tokenEndpoint = getAccessTokenEndpoint(authConfig);
        if (!tokenEndpoint) {
            // Misconfiguration, not a bad token - keep the credential and fall back to B2C login.
            console.log('refreshAccessTokenFromBiometric: no token endpoint configured for this platform');
            return false;
        }
        const scope = await getScope(false);
        const response = await postRefreshGrant(
            tokenEndpoint,
            authConfig.CLIENT_ID,
            refreshToken,
            scope
        );
        if (!response.ok) {
            console.log('refreshAccessTokenFromBiometric: token endpoint returned', response.status);
            // Stored refresh token is no longer usable; drop it so we stop offering biometric with a dead token.
            await deleteBiometricCredential();
            return false;
        }
        const data = response.data;
        if (!data.access_token) {
            // Auth succeeded but no access token for the requested resource scope -
            // usually a scope/registration issue, not a bad refresh token. Keep the
            // credential and fall back to B2C rather than silently logging in tokenless.
            console.log('refreshAccessTokenFromBiometric: no access_token in refresh response (check B2CUserFlowScope)');
            return false;
        }
        await storeAuthData(data.access_token);
        await set(B2C_SCOPE_CURRENT, scope);
        // B2C rotates refresh tokens; persist the new one for next time.
        if (data.refresh_token) {
            sessionRefreshToken = data.refresh_token;
            const username = (await getUserName()) || 'eam-user';
            await storeBiometricCredential(data.refresh_token, username);
        }
        return true;
    } catch (error) {
        console.log('refreshAccessTokenFromBiometric error:', error);
        return false;
    }
};

/**
 * Exchanges the stored PORTAL refresh token for a fresh portal access token and
 * saves it via storePortalApiToken, so post-login portal checks pass after a
 * biometric restore (even a long time after the session ended). Only reachable
 * when REFRESH_TOKEN_AVAILABLE is true.
 */
const refreshPortalTokenFromBiometric = async (): Promise<boolean> => {
    try {
        const portalRefreshToken = await getBiometricCredential(BIOMETRIC_PORTAL_SERVER);
        if (!portalRefreshToken) {
            console.log('refreshPortalTokenFromBiometric: no portal refresh token stored');
            return false;
        }
        const authConfig = await getAuthConfig();
        if (!authConfig) {
            return false;
        }
        const tokenEndpoint = getAccessTokenEndpoint(authConfig);
        if (!tokenEndpoint) {
            console.log('refreshPortalTokenFromBiometric: no token endpoint configured');
            return false;
        }
        const scope = await getScope(true);
        const response = await postRefreshGrant(
            tokenEndpoint,
            authConfig.CLIENT_ID,
            portalRefreshToken,
            scope
        );
        if (!response.ok) {
            console.log('refreshPortalTokenFromBiometric: token endpoint returned', response.status);
            // Portal refresh token no longer usable; drop it so we stop trying it.
            await deleteBiometricCredential(BIOMETRIC_PORTAL_SERVER);
            return false;
        }
        const data = response.data;
        if (!data.access_token) {
            console.log('refreshPortalTokenFromBiometric: no access_token in portal refresh response');
            return false;
        }
        await storePortalApiToken(data.access_token);
        // B2C rotates refresh tokens; persist the new portal one for next time.
        if (data.refresh_token) {
            sessionPortalRefreshToken = data.refresh_token;
            const username = (await getUserName()) || 'eam-user';
            await storeBiometricCredential(data.refresh_token, username, BIOMETRIC_PORTAL_SERVER);
        }
        return true;
    } catch (error) {
        console.log('refreshPortalTokenFromBiometric error:', error);
        return false;
    }
};

/**
 * Retrieves the authentication configuration.
 * @returns {Promise<any>} A promise that resolves to the authentication configuration object.
 */
export const getAuthConfig = async () => {
    try {
        const response = await fetch('/authConfig.json');
        if (!response.ok) {
            console.log('Failed to load auth configuration');
        }
        let iam = await getAuthTypePreference(_iamKey),
            authConfigs = await response.json(),
            authConfig = authConfigs.find((x: any) => x.IAM === iam?.value);
        return authConfig;
    } catch (error) {
        console.log('Error fetching auth config:', error);
        return null;
    }
}

export const validateBearerToken = async () => {
    try {
        const token = await getBearerToken();
        if (!!!token) {
            return false;
        }
        //check if aud is valid for the current api scope
        if (await isB2CRefreshRequired()) {
            return false;
        }
        const tokenParsed = await get(BEARER_TOKEN_PARSED);
        if (!!tokenParsed) {
            let today = new Date();
            return (today.valueOf() < tokenParsed.exp * 1000);
        }
        return false;
    } catch (error) {
        console.log('ERROR (validateUserToken): ', error);
        return false;
    }
};

const isB2CRefreshRequired = async () => {
    const apiSession = await getApiSession();
    const currentScope = await get(B2C_SCOPE_CURRENT);
    if (!!apiSession && !!apiSession.B2CUserFlowScope && !!currentScope) {
        // Full mode only: offline_access is appended to obtain a refresh token for
        // biometric silent re-login; it is not part of the API resource scope
        // identity, so ignore it here. In interim mode this uses the exact original
        // comparison - no behaviour change.
        if (REFRESH_TOKEN_AVAILABLE) {
            const normalizeScope = (scope: string) =>
                scope.replace(/offline_access/g, '').trim().replace(/\s+/g, ' ');
            return normalizeScope(currentScope) !== normalizeScope(apiSession.B2CUserFlowScope);
        }
        return currentScope !== apiSession.B2CUserFlowScope;
    }
    return false;
};

export const handlePWACallback = async (url: string, isPortal: boolean) => {
    const urlParams = JSON.stringify(getUrlParams(url));
    const pObj = JSON.parse(urlParams);
    const accessToken = pObj.access_token;
    if (accessToken !== undefined) {
        //store the token
        if (isPortal) {
            await storePortalAuthData(accessToken);
        } else {
            await storeAuthData(accessToken);
            //store b2c scope used
            getOAuthOptions(false).then((oAuthOptions) => {
                set(B2C_SCOPE_CURRENT, oAuthOptions.scope);
            });
        }
        return true;
    } else {
        console.log('Oauth Parameters where not present in return url');

        return false;
    }
};

const getOAuthOptions = async (isPortal: boolean, forceReauth: boolean = false) => {
    let authConfig = await getAuthConfig(),
        loginHint = getLoginHint(),
        domainHint = getDomainHint(),
        additionalParameters = {};
    if (forceReauth) {
        // Bug #879938: a portal-initiated launch supplies IAM but not the user's
        // email, so the launcher's identity is unknown. Without forcing it, the
        // IdP silently reuses any existing SSO session and signs the new launcher
        // in as the *previous* user. Force a fresh sign-in so the identity is
        // re-established.
        //
        // This MUST be checked before the login_hint branch. Hints are now also
        // read from a stored copy (see readHint), so a hint left by an earlier
        // login in this same session would otherwise suppress the prompt and
        // reintroduce exactly the cross-user sign-in this guards against.
        additionalParameters = {
            "prompt": "login"
        };
    } else if (loginHint !== null) {
        //domain_hint is what routes the IdP to the federated provider; omit the
        //key entirely when absent rather than sending a null the IdP will reject
        additionalParameters = {
            "login_hint": loginHint,
            ...(domainHint !== null ? { "domain_hint": domainHint } : {})
        };
    }
    if (!authConfig) return {};
    return {
        appId: authConfig.CLIENT_ID,
        authorizationBaseUrl: authConfig.AUTHORIZATION_BASE_URL,
        accessTokenEndpoint: authConfig.ACCESS_TOKEN_ENDPOINT,
        scope: await getScope(isPortal),
        logsEnabled: authConfig.LOGS_ENABLED,
        web: {
            pkceEnabled: authConfig.WEB.PKCE_ENABLED,
            responseType: authConfig.WEB.RESPONSE_TYPE,
            accessTokenEndpoint: authConfig.WEB.ACCESS_TOKEN_ENDPOINT,
            redirectUrl: (isPortal) ? authConfig.WEB.REDIRECT_URL_PORTAL : authConfig.WEB.REDIRECT_URL,
            windowTarget: authConfig.WEB.WINDOW_TARGET,
            additionalParameters: additionalParameters
        },
        android: {
            pkceEnabled: authConfig.ANDROID.PKCE_ENABLED,
            responseType: authConfig.ANDROID.RESPONSE_TYPE,
            redirectUrl: authConfig.ANDROID.REDIRECT_URL,
            accessTokenEndpoint: authConfig.ANDROID.ACCESS_TOKEN_ENDPOINT,
            handleResultOnNewIntent: authConfig.ANDROID.HANDLE_RESULT_ON_NEWINTENT,
            handleResultOnActivityResult: authConfig.ANDROID.HANDLE_RESULT_ON_ACTIVITYRESULT,
            additionalParameters: additionalParameters
        },
        ios: {
            pkceEnabled: authConfig.IOS.PKCE_ENABLED,
            responseType: authConfig.IOS.RESPONSE_TYPE,
            redirectUrl: authConfig.IOS.REDIRECT_URL,
            accessTokenEndpoint: authConfig.IOS.ACCESS_TOKEN_ENDPOINT,
            additionalParameters: additionalParameters
        }
    }
};

// Session-scoped copies of the identity hints captured on the email screen. The
// EAM sign-in runs after the router has navigated away from
// /AuthCallback?email=...&domainhint=..., so by then the query string is gone;
// these keys are what let the second authorize carry the same hints as the first.
export const LOGIN_HINT_KEY = 'AuthLoginHint';
export const DOMAIN_HINT_KEY = 'AuthDomainHint';

/**
 * Remember the identity hints for the whole login rather than just the first
 * authorize. Called from the email screen before it routes to /AuthCallback.
 */
export const setAuthHints = (email: string | null, domainHint: string | null) => {
    try {
        if (!!email) sessionStorage.setItem(LOGIN_HINT_KEY, email);
        if (!!domainHint) sessionStorage.setItem(DOMAIN_HINT_KEY, domainHint);
    } catch (error) {
        console.log('setAuthHints error:', error);
    }
}

/**
 * Drop the remembered hints. Must run on every sign-out: a stale login_hint
 * would pre-fill the next person's sign-in with the previous user's identity
 * (the failure mode guarded against in Bug #879938).
 */
export const clearAuthHints = () => {
    try {
        sessionStorage.removeItem(LOGIN_HINT_KEY);
        sessionStorage.removeItem(DOMAIN_HINT_KEY);
    } catch (error) {
        console.log('clearAuthHints error:', error);
    }
}

const readHint = (paramName: string, storageKey: string) => {
    let fromUrl: string | null = null;
    try {
        fromUrl = new URLSearchParams(new URL(window.location.href).search).get(paramName);
    } catch {
        fromUrl = null;
    }
    if (!!fromUrl) {
        //refresh the stored copy so a later authorize in this same login reuses it
        try {
            sessionStorage.setItem(storageKey, fromUrl);
        } catch {
            // sessionStorage unavailable - the URL value is still returned below
        }
        return fromUrl;
    }
    try {
        return sessionStorage.getItem(storageKey);
    } catch {
        return null;
    }
}

// Both hints are supplied for the EAM sign-in as well as the Portal one. Without
// them the second authorize reaches the IdP carrying no identity context, so it
// cannot resolve against the SSO session established moments earlier and renders
// a fresh login form - the user types their password twice for one sign-in.
const getLoginHint = () => {
    return readHint('email', LOGIN_HINT_KEY);
}

const getDomainHint = () => {
    return readHint('domainhint', DOMAIN_HINT_KEY);
}

const getScope = async (isPortal: boolean) => {
    if (isPortal) {
        let authConfig = await getAuthConfig();
        if (!authConfig) return '';
        let scope = authConfig.SCOPE_PORTAL;
        // Request a portal refresh token (native only) so biometric can silently
        // refresh the portal token. Web/PWA is never modified.
        if (REFRESH_TOKEN_AVAILABLE && isPlatform('hybrid') && !!scope && !scope.includes('offline_access')) {
            scope = `${scope} offline_access`;
        }
        return scope;
    } else {
        const apiSession = await getApiSession();
        let scope = apiSession.B2CUserFlowScope;
        // Request a refresh token for biometric silent re-login once B2C supports it.
        // Native only - biometric sign-in doesn't exist on web/PWA, so the PWA's
        // auth request is never modified, even in full mode.
        if (REFRESH_TOKEN_AVAILABLE && isPlatform('hybrid') && !!scope && !scope.includes('offline_access')) {
            scope = `${scope} offline_access`;
        }
        return scope;
    }
};

const storeAuthData = async (token: string) => {
    await clearAuthData();
    await set(BEARER_TOKEN, token);
    await set(BEARER_TOKEN_PARSED, parseJwt(token));
}

/**
 * Adopt the Portal sign-in's token as the EAM token instead of running a second
 * interactive authorize.
 *
 * On tenants where both sign-ins resolve to the same client AND the same scope
 * (ApteanIAM: SCOPE and SCOPE_PORTAL are both "openid profile offline_access",
 * and mobileBootstrap forces every application's B2CUserFlowScope to match), the
 * two authorizes are indistinguishable - the tokens they return carry the same
 * aud, azp, scope and sub, differing only in sid/auth_time. The second authorize
 * therefore buys nothing but a second credential prompt, because the IdP does not
 * reliably have a reusable browser session by the time it runs.
 *
 * Returns false when the scopes differ (B2C tenants, where the EAM scope is a
 * genuinely different resource), leaving the normal signin() path untouched.
 */
export const reusePortalSessionForEam = async (): Promise<boolean> => {
    try {
        //A token is only interchangeable if BOTH the issuing client and the scope
        //match - scope alone is not enough, because the token is copied into the
        //EAM slot and an audience minted for a different client would be wrong.
        //Today a single CLIENT_ID backs both authorizes, so this cannot diverge;
        //assert it from the resolved options anyway rather than relying on that,
        //so a portal-specific client added later disables reuse instead of
        //silently adopting a token issued for the wrong audience.
        const portalOptions: any = await getOAuthOptions(true);
        const eamOptions: any = await getOAuthOptions(false);
        if (!!!portalOptions?.appId || !!!eamOptions?.appId || portalOptions.appId !== eamOptions.appId) {
            return false;
        }
        const eamScope = await getScope(false);
        const portalScope = await getScope(true);
        const normalize = (scope: string) => (scope || '').trim().replace(/\s+/g, ' ');
        if (!!!eamScope || !!!portalScope || normalize(eamScope) !== normalize(portalScope)) {
            return false;
        }
        //note: signoutPortal() clears BEARER_TOKEN_PORTAL but deliberately leaves
        //PORTAL_API_TOKEN in place, which is why this still works after it runs
        if (!(await isPortalTokenValid())) {
            return false;
        }
        //Require real life left on the token, not merely "not expired yet". During
        //login it was minted seconds ago, but this also runs from Change Plant/DB
        //long afterwards - adopting a token with seconds remaining would hand the
        //user a session that dies immediately, where signin() would have minted a
        //fresh one. Fall through to the interactive path in that case.
        const parsed = await getPortalApiTokenParsed();
        const MIN_REMAINING_MS = 120000;
        if (!parsed?.exp || (parsed.exp * 1000) - new Date().valueOf() < MIN_REMAINING_MS) {
            console.log('reusePortalSessionForEam: portal token too close to expiry, falling back to sign-in');
            return false;
        }
        const portalToken = await getPortalApiToken();
        if (!!!portalToken) {
            return false;
        }
        await storeAuthData(portalToken);
        await set(B2C_SCOPE_CURRENT, eamScope);
        //keep biometric intact: with an identical client+scope the portal refresh
        //token redeems for the EAM token too, so enrollment/restore still have a
        //credential in the EAM slot that signin() would otherwise have cached.
        //NOTE: this deliberately leaves the SAME refresh token in both the EAM
        //and Portal slots. restoreSessionFromBiometric must therefore never
        //redeem both - it adopts the refreshed Portal token for EAM instead.
        //Redeeming twice presents a spent token wherever the IdP rotates.
        if (REFRESH_TOKEN_AVAILABLE && !!sessionPortalRefreshToken) {
            sessionRefreshToken = sessionPortalRefreshToken;
            const username = (await getUserName()) || 'eam-user';
            await storeBiometricCredential(sessionPortalRefreshToken, username);
        }
        console.log('reusePortalSessionForEam: adopted portal token as EAM token (identical client and scope)');
        return true;
    } catch (error) {
        console.log('reusePortalSessionForEam error:', error);
        return false;
    }
}

const storePortalAuthData = async (token: string, idToken: string = '') => {
    await clearPortalAuthData();
    await set(BEARER_TOKEN_PORTAL, token);
    await set(BEARER_TOKEN_PORTAL_PARSED, parseJwt(token));
    await set(ID_TOKEN, idToken);
    await storePortalApiToken(token);
    //reset mobilebootstrap data and apisession for new user
    await updatePortalUserData();
}

export const clearAuthDataAndIamPreference = async () => {
    await clearAuthData();
    await remove(_iamKey);
}

export const clearAuthData = async () => {
    await remove(BEARER_TOKEN);
    await remove(BEARER_TOKEN_PARSED);
    await remove(B2C_SCOPE_CURRENT);
}

const clearPortalAuthData = async () => {
    await remove(BEARER_TOKEN_PORTAL);
    await remove(BEARER_TOKEN_PORTAL_PARSED);
}

export const getPortalApiToken = async () => {
    return await get(PORTAL_API_TOKEN).then(result => {
        if (!!!result) {
            console.log('Portal API token not found');
        }
        return result;
    });
}

export const getPortalApiTokenParsed = async () => {
    return await get(PORTAL_API_TOKEN_PARSED).then(result => {
        if (!!!result) {
            console.log('Portal API token parsed not found');
        }
        return result;
    });
}

/**
 * Whether the stored Portal API token is present and unexpired. Used alongside
 * validateBearerToken so the "session still valid, skip biometric" fast path
 * only applies when BOTH tokens are good. If the portal token lapsed (it can
 * expire slightly before the EAM token), we treat the session as needing
 * re-auth so biometric is offered instead of slipping through to a B2C login.
 */
export const isPortalTokenValid = async (): Promise<boolean> => {
    try {
        const parsed = await getPortalApiTokenParsed();
        if (!parsed || !parsed.exp) {
            return false;
        }
        return (new Date().valueOf() < parsed.exp * 1000);
    } catch (error) {
        console.log('isPortalTokenValid error:', error);
        return false;
    }
};

const storePortalApiToken = async (token: string) => {
    await clearPortalApiToken();
    await set(PORTAL_API_TOKEN, token);
    await set(PORTAL_API_TOKEN_PARSED, parseJwt(token));
    console.log('Portal API token stored for persistent use');
}

const clearPortalApiToken = async () => {
    await remove(PORTAL_API_TOKEN);
    await remove(PORTAL_API_TOKEN_PARSED);
    console.log('Portal API token cleared');
}

const updatePortalUserData = async () => {
    const jwtPortalParsed = await get(BEARER_TOKEN_PORTAL_PARSED);
    const portalUserUniqueId: string = await get(PORTAL_USER_UNIQUEID);
    if (jwtPortalParsed.sub !== portalUserUniqueId) {
        //clear bootstrap data and api session data
        await clearApiSession();
        await clearApiOptionsData();
        //update user uniqueid
        await remove(PORTAL_USER_UNIQUEID);
        await set(PORTAL_USER_UNIQUEID, jwtPortalParsed.sub);
    }
};

const getLogoutUrl = async () => {
    let authConfig = await getAuthConfig();
    let logoutUrl = globalThis.customLogOutUrl;
    if (!authConfig) return '';
    if (isPlatform('hybrid')) {
        let idToken = await get(ID_TOKEN);
        if (isPlatform('android')) {
            authConfig.ANDROID.POST_LOGOUT_REDIRECT_URL = logoutUrl || authConfig.ANDROID.POST_LOGOUT_REDIRECT_URL;
            return `${authConfig.LOGOUT_URL}?client_id=${authConfig.CLIENT_ID}&response_type=${authConfig.ANDROID.RESPONSE_TYPE}&post_logout_redirect_uri=${authConfig.ANDROID.POST_LOGOUT_REDIRECT_URL}&scope=${authConfig.SCOPE}&id_token_hint=${idToken}`;
        } else if (isPlatform('ios')) {
            authConfig.IOS.POST_LOGOUT_REDIRECT_URL = logoutUrl || authConfig.IOS.POST_LOGOUT_REDIRECT_URL;
            return `${authConfig.LOGOUT_URL}?client_id=${authConfig.CLIENT_ID}&response_type=${authConfig.IOS.RESPONSE_TYPE}&post_logout_redirect_uri=${authConfig.IOS.POST_LOGOUT_REDIRECT_URL}&scope=${authConfig.SCOPE}&id_token_hint=${idToken}`;
        }
    }
    authConfig.WEB.POST_LOGOUT_REDIRECT_URL = logoutUrl || authConfig.WEB.POST_LOGOUT_REDIRECT_URL;
    return `${authConfig.LOGOUT_URL}?client_id=${authConfig.CLIENT_ID}&response_type=${authConfig.WEB.RESPONSE_TYPE}&post_logout_redirect_uri=${authConfig.WEB.POST_LOGOUT_REDIRECT_URL}&scope=${authConfig.SCOPE}`;
}

const parseJwt = (token: string) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}

const getUrlParams = (url: string) => {
    const urlString = `${url}`.trim();
    if (urlString.length === 0) {
        return;
    }
    // #132
    let hashIndex = urlString.indexOf("#");
    let queryIndex = urlString.indexOf("?");
    if (hashIndex === -1 && queryIndex === -1) {
        return;
    }
    let paramsIndex;
    if (hashIndex > -1 && queryIndex === -1) {
        paramsIndex = hashIndex;
    }
    else if (queryIndex > -1 && hashIndex === -1) {
        paramsIndex = queryIndex;
    }
    else {
        paramsIndex = hashIndex > -1 && hashIndex < queryIndex ? hashIndex : queryIndex;
    }
    if (urlString.length <= paramsIndex + 1) {
        return;
    }
    const urlParamStr = urlString.slice(paramsIndex + 1);
    const keyValuePairs = urlParamStr.split(`&`);
    // @ts-ignore
    return keyValuePairs.reduce((accumulator, currentValue) => {
        const [key, val] = currentValue.split(`=`);
        if (key && key.length > 0) {
            return Object.assign(Object.assign({}, accumulator), { [key]: decodeURIComponent(val) });
        }
    }, {});
}